import { defineMessages } from 'react-intl';
import { invert, uniq, without } from 'lodash';

import { MultiselectDropdownItem } from '@app/components';
import { sharedMessages } from '@app/translations';

import { facet, Operator } from '../../constants';

export enum AdvancedSearchField {
  keyword = 'keyword',
  titleAll = 'titleAll',
  titleMain = 'titleMain',
  authorCreator = 'authorCreator',
  subjectAll = 'subjectAll',
  nameAll = 'nameAll',
  namePersonal = 'namePersonal',
  identifiers = 'lccnIsbnIssn',
  series = 'series',
}

export const advancedSearchFields = Object.values(AdvancedSearchField);

export const advancedSearchOptions = {
  keyword: 'keyword',
  title: 'allTitles',
  titleMain: 'titleMain',
  contributors: 'contributors.name',
  subjects: 'subjects.value',
  nameAll: 'allNames',
  namePersonal: 'personalContributors',
  identifiers: 'identifiers.value',
  series: 'series.value',
};

export const fieldsToSearchOptionMap = {
  [AdvancedSearchField.keyword]: advancedSearchOptions.keyword,
  [AdvancedSearchField.titleAll]: advancedSearchOptions.title,
  [AdvancedSearchField.titleMain]: advancedSearchOptions.titleMain,
  [AdvancedSearchField.authorCreator]: advancedSearchOptions.contributors,
  [AdvancedSearchField.subjectAll]: advancedSearchOptions.subjects,
  [AdvancedSearchField.nameAll]: advancedSearchOptions.nameAll,
  [AdvancedSearchField.namePersonal]: advancedSearchOptions.namePersonal,
  [AdvancedSearchField.identifiers]: advancedSearchOptions.identifiers,
  [AdvancedSearchField.series]: advancedSearchOptions.series,
};

export const fieldsToMaskMap = {
  [AdvancedSearchField.keyword]: 'keyword',
  [AdvancedSearchField.titleAll]: 'titleAll',
  [AdvancedSearchField.titleMain]: 'titleMain',
  [AdvancedSearchField.authorCreator]: 'contributor',
  [AdvancedSearchField.subjectAll]: 'subject',
  [AdvancedSearchField.nameAll]: 'nameAll',
  [AdvancedSearchField.namePersonal]: 'namePersonal',
  [AdvancedSearchField.identifiers]: 'identifier',
  [AdvancedSearchField.series]: 'series',
};

export enum ConsortiaAdvancedSearchField {
  keyword = 'keyword',
  title = 'title',
  author = 'author',
  isbn = 'isbn',
  subject = 'subject',
}

export const consortiaAdvancedSearchFields = Object.values(
  ConsortiaAdvancedSearchField
);

export const consortiaFieldsToMaskMap = {
  [ConsortiaAdvancedSearchField.keyword]: 'keyword',
  [ConsortiaAdvancedSearchField.title]: 'title',
  [ConsortiaAdvancedSearchField.author]: 'contributors',
  [ConsortiaAdvancedSearchField.isbn]: 'isbn',
  [ConsortiaAdvancedSearchField.subject]: 'subjects',
};

export const consortiaFieldsRegex = uniq(
  Object.values(consortiaFieldsToMaskMap)
).join('|');

export const searchOptionToFieldMap = invert(fieldsToSearchOptionMap);

export enum AdvancedSearchQualifier {
  containsAll = 'containsAll',
  containsAny = 'containsAny',
  exactPhrase = 'exactPhrase',
  startsWith = 'startsWith',
  equal = 'equal',
}

export const consortiaFieldToQualifierMap = {
  [ConsortiaAdvancedSearchField.keyword]: AdvancedSearchQualifier.containsAll,
  [ConsortiaAdvancedSearchField.title]: AdvancedSearchQualifier.equal,
  [ConsortiaAdvancedSearchField.author]: AdvancedSearchQualifier.equal,
  [ConsortiaAdvancedSearchField.isbn]: AdvancedSearchQualifier.equal,
  [ConsortiaAdvancedSearchField.subject]: AdvancedSearchQualifier.equal,
};

export const consortiaSearchOperatorMap = {
  [` ${Operator.all} `]: AdvancedSearchQualifier.containsAll,
  [Operator.equal]: AdvancedSearchQualifier.equal,
};

export const consortiaQualifiersRegex = Object.keys(
  consortiaSearchOperatorMap
).join('|');

export const advancedSearchQualifiers = without(
  Object.keys(AdvancedSearchQualifier),
  AdvancedSearchQualifier.equal
);

export const searchOperatorMap = {
  [AdvancedSearchQualifier.containsAll]: Operator.all,
  [AdvancedSearchQualifier.containsAny]: Operator.any,
  [AdvancedSearchQualifier.exactPhrase]: Operator.phraseMatch,
  [AdvancedSearchQualifier.startsWith]: Operator.all,
  [AdvancedSearchQualifier.equal]: Operator.equal,
};

export const searchQualifierMaskMap = {
  [AdvancedSearchQualifier.containsAll]: 'containsAll',
  [AdvancedSearchQualifier.containsAny]: 'containsAny',
  [AdvancedSearchQualifier.exactPhrase]: 'exactPhrase',
  [AdvancedSearchQualifier.startsWith]: 'startsWith',
  [AdvancedSearchQualifier.equal]: 'equal',
};

export enum BooleanOperator {
  and = 'and',
  or = 'or',
  not = 'not',
}

export const advancedSearchMessages = defineMessages({
  [BooleanOperator.and]: {
    id: 'advancedSearch.and',
    defaultMessage: 'And',
  },
  [BooleanOperator.or]: {
    id: 'advancedSearch.or',
    defaultMessage: 'Or',
  },
  [BooleanOperator.not]: {
    id: 'advancedSearch.not',
    defaultMessage: 'Not',
  },
  [AdvancedSearchQualifier.containsAll]: {
    id: 'advancedSearch.containsAll',
    defaultMessage: 'Contains all',
  },
  [AdvancedSearchQualifier.containsAny]: {
    id: 'advancedSearch.containsAny',
    defaultMessage: 'Contains any',
  },
  [AdvancedSearchQualifier.exactPhrase]: {
    id: 'advancedSearch.exactPhrase',
    defaultMessage: 'Exact phrase',
  },
  [AdvancedSearchQualifier.startsWith]: {
    id: 'advancedSearch.startsWith',
    defaultMessage: 'Starts with',
  },
  [AdvancedSearchField.titleAll]: {
    id: 'advancedSearch.titleAll',
    defaultMessage: 'Title: all',
  },
  [AdvancedSearchField.titleMain]: {
    id: 'advancedSearch.titleMain',
    defaultMessage: 'Title: main',
  },
  [AdvancedSearchField.authorCreator]: {
    id: 'advancedSearch.authorCreator',
    defaultMessage: 'Author/creator',
  },
  [AdvancedSearchField.subjectAll]: {
    id: 'advancedSearch.subjectAll',
    defaultMessage: 'Subject: all',
  },
  [AdvancedSearchField.nameAll]: {
    id: 'advancedSearch.nameAll',
    defaultMessage: 'Name: all',
  },
  [AdvancedSearchField.namePersonal]: {
    id: 'advancedSearch.namePersonal',
    defaultMessage: 'Name: personal',
  },
  [AdvancedSearchField.identifiers]: {
    id: 'advancedSearch.identifiers',
    defaultMessage: 'Identifiers (ISBN, ISSN, LCCN, etc)',
  },
  [ConsortiaAdvancedSearchField.author]: {
    id: 'advancedSearch.author',
    defaultMessage: 'Author',
  },
  [AdvancedSearchField.keyword]: sharedMessages.keyword,
  [AdvancedSearchField.series]: sharedMessages.series,
  [ConsortiaAdvancedSearchField.title]: sharedMessages.title,
  [ConsortiaAdvancedSearchField.subject]: sharedMessages.subject,
  [ConsortiaAdvancedSearchField.isbn]: sharedMessages.isbn,
});

export const defaultAdvancedSearchRowState = {
  value: '',
  field: AdvancedSearchField.keyword,
  qualifier: AdvancedSearchQualifier.containsAll,
};

export const maxRowCount = 7;

export const typesMap = {
  booleanOperator: [
    BooleanOperator.and,
    BooleanOperator.or,
    BooleanOperator.not,
  ],
  field: advancedSearchFields,
  qualifier: advancedSearchQualifiers,
};

export const advancedSearchFormRowCount = 3;

export const booleanOperatorRegex = '( AND | OR | NOT )';

export const fieldsRegex = uniq(Object.values(fieldsToMaskMap)).join('|');

export const qualifiersRegex = Object.values(searchQualifierMaskMap).join('|');

export type SearchLimiterKeys =
  | typeof facet.format
  | typeof facet.language
  | typeof facet.location;

export type SearchLimiters = Record<
  SearchLimiterKeys,
  MultiselectDropdownItem[] | undefined
>;

export const limitersDefaultValue = {
  [facet.format]: [] as MultiselectDropdownItem[],
  [facet.language]: [] as MultiselectDropdownItem[],
  [facet.location]: [] as MultiselectDropdownItem[],
};

export type UnmappedParsedSearchLimiters = Record<
  SearchLimiterKeys,
  string[] | null
>;

export const defaultUnmappedLimitersValue = {
  [facet.format]: [],
  [facet.language]: [],
  [facet.location]: [],
};

export interface RowValues {
  value: string;
  field: AdvancedSearchField;
  qualifier: AdvancedSearchQualifier;
  booleanOperator?: BooleanOperator;
  isInvalid?: boolean;
}

export type FormValues = Record<number, RowValues>;
